import React, { useEffect, useState, useMemo } from "react";
import { useAxios } from "../../../common_nextgen/axios/AxiosHelper";
import useCustomNavigate from "../../../common_nextgen/common_components/Navigation/NextGenNavigation";
import getBackendUrl from "../../../environment/BackendService";
import { useLocation } from "react-router-dom";
import { ROUTE_PATHS } from "../../../common_nextgen/utils/Constants";
import { useToast } from "../../../common_nextgen/hooks/ToastContext";
import {
  downloadCSV,
  navigateToPresto,
} from "../../../common_nextgen/utils/UtilFunctions";

const usePharmacyNetworkForDiscountTable = () => {
  const { axios_helper_no_header } = useAxios();
  const [data, setData] = useState([]);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowData, setRowData] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const navigateToPage = useCustomNavigate();
  const [dropdownValues, setDropdownValues] = useState({});
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState("no");
  const toast = useToast();
  const location = useLocation();

  const [stateVal, setStateVal] = useState(location.state || []);
  const { currentState, previousState } = useMemo(() => {
    let [currentState = {}] = [...stateVal].reverse();
    const previousState = [...stateVal];
    previousState.splice(stateVal.length - 1, 1);
    return {
      previousState,
      currentState,
    };
  }, [stateVal]);

  const {
    selectedSchedules = [],
    previousScreen = ROUTE_PATHS.MAX_COPAY_SCHEDULE,
    currentIndex: initialIndex = 0,
    type = "",
    externalMode: initialExternalMode = false,
  } = currentState;
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [externalMode, setExternalMode] = useState(initialExternalMode);

  const [paramsData, setParamsData] = useState({
    dp3DiscountTableId: selectedSchedules?.[initialIndex]?.dp3DiscountTableId,
    dp3DiscountTableDesc: selectedSchedules?.[initialIndex]?.dp3DiscountTableDesc,
  });

  const {
    ADD_PHARMACY_NETWORK,
    EDIT_PHARMACY_NETWORK,
    DISPLAY_PHARMACY_NETWORK,
    COPY_PHARMACY_NETWORK,
    PHARMACY_NETWORK_NOTE,
    PHARMACY,
  } = ROUTE_PATHS;

  const screen_title = "Pharmacy Network for Discount Table";
  const screenNumber = "RCDUW001";

  const formFields = [
    {
      fieldLabel: "DP3 Discount Table ID",
      name: "dp3DiscountTableId",
      label: "Discount Table",
      type: "text",
      fieldNature: "readOnly",
      value: "",
      maxLength: 10,
    },
    {
      fieldLabel: "DP3 Discount Table Desc",
      name: "dp3DiscountTableDesc",
      label: "Description",
      type: "text",
      fieldNature: "readOnly",
      value: "",
      maxLength: 25,
    },
  ];

  const ctaButtons = [
    {
      button_label: "Export as CSV",
      button_handeler: () => {
        exportAsCSV();
      },
      button_key: "",
    },
  ];

  const menuOptions = [
    {
      label: "Display",
      icon: <span className="material-icons">visibility</span>,
      command: () => {
        navigateToPresto(screenNumber, {
          carCarrierId: rowData.carrier,
          regRegionCode: rowData.region,
          phnSeqNbr: rowData.seqNbr,
          phnPharmacyNetworkId: rowData.networkId,
          phnNetworkType: rowData.typeId,
          phnPharmacyNetworkName: rowData.description,
          fastExitReturnCode: "Y2U3948",
        });
      },
    },
    {
      label: "Pharmacy",
      icon: <span className="material-icons">local_pharmacy</span>,
      command: () => {
        navigateToPharmacy();
      },
    },
  ];

  const navigateToPharmacy = () => {
    const params = {
      carCarrierId: rowData.carrier,
      regRegionCode: rowData.region,
    };
    axios_helper_no_header(
      "GET",
      "api/phdDiscountPatPayPharmacy/validatePharmacyNavigation",
      null,
      params
    )
      .then((response) => {
        navigateToPage(PHARMACY, [
          ...previousState,
          { ...currentState, currentIndex, externalMode },
          {
            selectedSchedules: selectedRows.length ? selectedRows : [rowData],
            previousScreen: location.pathname,
          },
        ]);
      })
      .catch((error) => {
        const { responseStatus: { displayErrorFields = null, message } = {} } =
          error.data;
        toast.current.show({
          severity: "error",
          summary: "",
          detail: message,
          life: 3000,
        });
      });
  };

  /* Top Left Back button info that needs to be displayed on the page*/
  const leftButtons = [
    {
      button_label: "Back",
      button_handeler: () => {
        if (externalMode) {
          const message = {
            message: "navigate-to-external-program",
            url: previousScreen,
            parameters: {
              state: [...previousState],
            },
          };
          console.log("back messages", message);
          window.parent.postMessage(message, "*");
        } else if (currentIndex < selectedSchedules.length - 1) {
          setCurrentIndex(currentIndex + 1);
        } else {
          navigateToPage(previousScreen, previousState);
        }
      },
      button_key: "F12",
    },
  ];

  const columnDef = [
    {
      header: "Type",
      field: "typeId",
    },
    {
      header: "Network",
      field: "networkId",
    },
    {
      header: "Carrier",
      field: "carrier",
    },
    {
      header: "Region",
      field: "region",
    },
    {
      header: "Description",
      field: "description",
    },
  ];

  const fetchData = async () => {
    const page = first / rows;
    try {
      const raw_response = await axios_helper_no_header(
        "GET",
        `api/pharmacyNetworks`,
        null,
        {
          discountTableId: selectedSchedules[currentIndex].dp3DiscountTableId,
          page: page,
          size: rows,
        }
      );
      const response = raw_response.responseData;
      const pageResponse = raw_response.responsePaginationDTO;
      if (response.length === 0) {
        setData([]);
        setTotalRecords(0);
        toast.current.show({
          severity: "info",
          summary: "",
          detail: response.responseStatus.message,
          life: 3000,
        });
      } else {
        setData(response);
        setTotalRecords(pageResponse.totalRecords);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setData([]);
      setTotalRecords(0);
    }
  };

  useEffect(() => {
    
   if (!selectedSchedules?.[currentIndex]?.dp3DiscountTableId) return;
    fetchData();
  }, [paramsData, rows, currentPage, selectedOption, selectedSchedules]);

  useEffect(() => {
    setParamsData({
      dp3DiscountTableId: selectedSchedules?.[currentIndex]?.dp3DiscountTableId,
      dp3DiscountTableDesc:
        selectedSchedules?.[currentIndex]?.dp3DiscountTableDesc,
    });
  }, [currentIndex]);

  const onPageChange = (event) => {
    setFirst(event.first);
    setCurrentPage(event.page);
    setRows(event.rows);
  };

  const exportAsCSV = async () => {
    const queryParams = new URLSearchParams({
      discountTableId: selectedSchedules[initialIndex].dp3DiscountTableId,
      screenNumber: "RCDUW001",
    });

    try {
      const response = await axios_helper_no_header(
        "GET",
        "api/pharmacyNetworks/exportCsv",
        null,
        queryParams,
        "blob"
      );
      downloadCSV(response);
    } catch (error) {
      console.error("Error exporting as CSV:", error);
      toast.current.show({
        severity: "error",
        summary: "",
        detail: "Failed to export CSV. Please try again.",
        life: 3000,
      });
    }
  };

  const updateParamData = (data) => {
    setFirst(0);
    setCurrentPage(0);
    setParamsData(data);
  };

  const handleMessage = (event) => {
    const { type, data } = event.data;
    console.log("iframe load", type, data);
    if (type === "set-params") {
      const { currentIndex = 0, selectedSchedules = []} = data[data.length - 1] || {};
      setStateVal(data);
      setExternalMode(true);
      if (selectedSchedules.length) {
        setParamsData({
          dp3DiscountTableId: selectedSchedules?.[currentIndex]?.dp3DiscountTableId,
          dp3DiscountTableDesc: selectedSchedules?.[currentIndex]?.dp3DiscountTableDesc,
        });
      }
    }
    console.log("message", event.data);
  };

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  });

  return {
    screenNumber,
    screen_title,
    formFields,
    dropdownValues,
    paramsData,
    updateParamData,
    ctaButtons,
    leftButtons,
    data,
    menuOptions,
    columnDef,
    setRowData,
    selectedRows,
    setSelectedRows,
    first,
    rows,
    totalRecords,
    currentPage,
    onPageChange,
    isConfirmationModalOpen,
    setSelectedOption,
  };
};

export default usePharmacyNetworkForDiscountTable;